import { useCallback, useEffect, useState } from 'react';
import type { FormEvent, ReactNode } from 'react';
import '../styles/staff.css';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { STAFF_LINKS } from '../lib/staffLinks';
import { Icon, useStaffIcons } from '../components/Icon';
import { ReportsPage } from './StaffReports';
import { DeletionsPage } from './StaffDeletions';
import {
  clearToken,
  fetchOverview,
  getToken,
  refreshSession,
  requestCode,
  verifyCode,
  StaffError,
  type Overview,
  type Point,
} from '../lib/staffClient';
import {
  fmtInt,
  fmtPercent,
  fmtUsd,
  seriesDelta,
  sparkPoints,
  statusLabel,
} from '../lib/staffFormat';

/** Internal page: keep it out of search results even though nothing links here. */
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
}

function Tile({
  label,
  value,
  sub,
  definition,
}: {
  label: string;
  value: ReactNode;
  sub?: string;
  definition: string;
}) {
  return (
    <div className="staff-tile">
      <p className="staff-tile__label">{label}</p>
      <p className="staff-tile__value">{value}</p>
      {sub && <p className="staff-tile__sub">{sub}</p>}
      <p className="staff-tile__def">{definition}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="staff-section">
      <h2 className="staff-h2">{title}</h2>
      {children}
    </section>
  );
}

function Spark({
  title,
  points,
  field,
  definition,
  format = fmtInt,
}: {
  title: string;
  points: Point[];
  field: string;
  definition: string;
  format?: (n: number) => string;
}) {
  const values = points.map((p) => Number(p[field]) || 0);
  const delta = seriesDelta(values);
  return (
    <div className="staff-tile">
      <p className="staff-tile__label">{title}</p>
      <p className="staff-tile__value">
        {format(values[values.length - 1] ?? 0)}
        <span className="staff-tile__delta">
          {' '}
          {delta >= 0 ? '+' : ''}
          {format(delta)} over period
        </span>
      </p>
      <svg
        viewBox="0 0 200 48"
        className="staff-spark"
        role="img"
        aria-label={`${title} over time`}
      >
        <polyline
          points={sparkPoints(values, 200, 44)}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        />
      </svg>
      <p className="staff-tile__def">
        {points[0]?.date} to {points[points.length - 1]?.date}. {definition}
      </p>
    </div>
  );
}

/** Snapshot-backed series: history starts at the first snapshot, so say so
 *  instead of drawing a flat line from zero. */
function SnapshotSpark(props: {
  title: string;
  points: Point[];
  field: string;
  definition: string;
}) {
  if (props.points.length < 2) {
    return (
      <div className="staff-tile">
        <p className="staff-tile__label">{props.title}</p>
        <p className="staff-tile__def">
          Collecting: {props.points.length} daily snapshot so far, need 2 for a trend.{' '}
          {props.definition}
        </p>
      </div>
    );
  }
  return <Spark {...props} />;
}

function SignIn({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [otpId, setOtpId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (!otpId) setOtpId(await requestCode(email.trim()));
      else {
        await verifyCode(otpId, code.trim());
        onDone();
      }
    } catch (err) {
      setError(err instanceof StaffError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="skf-card staff-signin" onSubmit={submit}>
      <h1 className="skf-title">Staff</h1>
      <p className="skf-subtitle">
        {otpId ? 'Enter the code we emailed you.' : 'Sign in with your Skiffs account email.'}
      </p>
      {!otpId ? (
        <input
          className="staff-input"
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      ) : (
        <input
          className="staff-input"
          inputMode="numeric"
          required
          autoComplete="one-time-code"
          placeholder="Code"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      )}
      {error && (
        <p className="staff-error" role="alert">
          {error}
        </p>
      )}
      <button className="btn btn--block" type="submit" disabled={busy}>
        {otpId ? 'Sign in' : 'Email me a code'}
      </button>
    </form>
  );
}

export type StaffTab = 'metrics' | 'reports' | 'deletions';

const TABS: { key: StaffTab; to: string; label: string; icon: string }[] = [
  { key: 'metrics', to: '/staff', label: 'Metrics', icon: 'bar-chart-line' },
  { key: 'reports', to: '/staff/reports', label: 'Reports', icon: 'flag' },
  { key: 'deletions', to: '/staff/deletions', label: 'Deletions', icon: 'person-x' },
];

export function Staff({ tab = 'metrics' }: { tab?: StaffTab }) {
  useDocumentTitle('Staff — Skiffs');
  useNoIndex();
  useStaffIcons();
  const [signedIn, setSignedIn] = useState(() => getToken() !== null);

  // Slide the session on every load; only an explicit 401/403 signs out.
  useEffect(() => {
    if (!signedIn) return;
    void refreshSession().then((ok) => {
      if (!ok) setSignedIn(false);
    });
  }, [signedIn]);

  if (!signedIn)
    return (
      <div className="staff-wrap">
        <SignIn onDone={() => setSignedIn(true)} />
      </div>
    );

  return (
    <div className="staff-wrap">
      <nav className="staff-nav" aria-label="Staff">
        <div className="staff-tabs">
          {TABS.map((t) => (
            <Link
              key={t.key}
              to={t.to}
              className={`staff-tab${t.key === tab ? ' staff-tab--on' : ''}`}
              aria-current={t.key === tab ? 'page' : undefined}
            >
              <Icon name={t.icon} />
              {t.label}
            </Link>
          ))}
        </div>
        <div className="staff-links">
          <select
            aria-label="Open an operator tool in a new tab"
            className="staff-input"
            value=""
            onChange={(e) => {
              if (e.target.value) window.open(e.target.value, '_blank', 'noopener,noreferrer');
            }}
          >
            <option value="">Tools ↗</option>
            {STAFF_LINKS.map((l) => (
              <option key={l.href} value={l.href}>
                {l.label}
              </option>
            ))}
          </select>
          <button
            className="btn btn--ghost btn--icon"
            aria-label="Sign out"
            title="Sign out"
            onClick={() => {
              clearToken();
              setSignedIn(false);
            }}
          >
            <Icon name="box-arrow-right" />
          </button>
        </div>
      </nav>
      {tab === 'metrics' && <MetricsPage onUnauthorized={() => setSignedIn(false)} />}
      {tab === 'reports' && <ReportsPage onUnauthorized={() => setSignedIn(false)} />}
      {tab === 'deletions' && <DeletionsPage onUnauthorized={() => setSignedIn(false)} />}
    </div>
  );
}

function MetricsPage({ onUnauthorized }: { onUnauthorized: () => void }) {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await fetchOverview(days));
    } catch (err) {
      if (err instanceof StaffError && err.status === 401) onUnauthorized();
      else setError(err instanceof StaffError ? err.message : 'Could not load the dashboard.');
    }
  }, [days, onUnauthorized]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <header className="staff-top">
        <h1 className="skf-title">Skiffs activity</h1>
        <div className="staff-actions">
          <select
            aria-label="Chart period"
            className="staff-input"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[7, 30, 90, 180].map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
          <button
            className="btn btn--ghost btn--icon"
            aria-label="Refresh"
            title="Refresh"
            onClick={() => void load()}
          >
            <Icon name="arrow-clockwise" />
          </button>
        </div>
      </header>
      {error && (
        <p className="staff-error" role="alert">
          {error}
        </p>
      )}
      {!data && !error && <p className="skf-meta">Loading…</p>}
      {data && <Dashboard d={data} />}
    </>
  );
}

function Dashboard({ d }: { d: Overview }) {
  const { users, skiffs, memberships, ai, series } = d;
  return (
    <>
      <p className="skf-meta">
        Updated {new Date(d.generated_at).toLocaleString()}. {d.exclusions}
      </p>

      <Section title="Users">
        <div className="staff-grid">
          <Tile
            label="Total users"
            value={fmtInt(users.total.value)}
            definition={users.total.definition}
          />
          <Tile
            label="Active this hour"
            value={fmtInt(users.hourly_active.value)}
            sub={users.hourly_active.window}
            definition={users.hourly_active.definition}
          />
          <Tile
            label="Daily active (DAU)"
            value={fmtInt(users.daily_active.value)}
            sub={users.daily_active.window}
            definition={users.daily_active.definition}
          />
          <Tile
            label="Weekly active (WAU)"
            value={fmtInt(users.weekly_active.value)}
            sub={users.weekly_active.window}
            definition={users.weekly_active.definition}
          />
          <Tile
            label="Monthly active (MAU)"
            value={fmtInt(users.monthly_active.value)}
            sub={users.monthly_active.window}
            definition={users.monthly_active.definition}
          />
          <Tile
            label="Stickiness (DAU ÷ MAU)"
            value={fmtPercent(users.stickiness.value)}
            definition={users.stickiness.definition}
          />
        </div>
      </Section>

      <Section title="Growth over time">
        <div className="staff-grid">
          <Spark
            title="Total users"
            points={series.total_users.points}
            field="total"
            definition={series.total_users.definition}
          />
          <Spark
            title="New signups per day"
            points={series.signups.points}
            field="n"
            definition={series.signups.definition}
          />
          <Spark
            title="Daily active users"
            points={series.daily_active.points}
            field="n"
            definition={series.daily_active.definition}
          />
          <Spark
            title="Skiffs created per day"
            points={series.skiffs_created.points}
            field="n"
            definition={series.skiffs_created.definition}
          />
        </div>
      </Section>

      <Section title="Skiffs">
        <div className="staff-grid">
          {(['active', 'ended', 'other'] as const).map((k) => (
            <Tile
              key={k}
              label={
                k === 'active'
                  ? 'Active skiffs'
                  : k === 'ended'
                    ? 'Landed / ended skiffs'
                    : 'Unexpected status'
              }
              value={fmtInt(skiffs[k].total)}
              sub={
                Object.entries(skiffs[k].by_status)
                  .map(([s, n]) => `${statusLabel(s)}: ${fmtInt(n)}`)
                  .join(' · ') || undefined
              }
              definition={skiffs[k].definition}
            />
          ))}
        </div>
        <SnapshotSpark
          title="Active skiffs over time"
          points={series.skiffs_by_status.points}
          field="active"
          definition={series.skiffs_by_status.definition}
        />
      </Section>

      <Section title="Memberships">
        <div className="staff-grid">
          <Tile
            label="Total members"
            value={fmtInt(memberships.total.value)}
            definition={memberships.total.definition}
          />
          <Tile
            label="Monthly"
            value={fmtInt(memberships.monthly.value)}
            definition={memberships.monthly.definition}
          />
          <Tile
            label="Annual"
            value={fmtInt(memberships.annual.value)}
            definition={memberships.annual.definition}
          />
          <Tile
            label="Lifetime"
            value={fmtInt(memberships.lifetime.value)}
            definition={memberships.lifetime.definition}
          />
          <Tile
            label="Pro without a subscription"
            value={fmtInt(memberships.pro_without_subscription.value)}
            definition={memberships.pro_without_subscription.definition}
          />
        </div>
        <SnapshotSpark
          title="Members over time"
          points={series.memberships.points}
          field="total"
          definition={series.memberships.definition}
        />
      </Section>

      <Section title="AI">
        <div className="staff-grid">
          {(
            [
              ['Requests', ai.requests, fmtInt],
              ['Provider calls', ai.llm_calls, fmtInt],
              ['Estimated cost', ai.cost_usd, fmtUsd],
            ] as const
          ).map(([label, w, f]) => (
            <Tile
              key={label}
              label={label}
              value={f(w.last_24h)}
              sub={`last 24h · 7 days: ${f(w.last_7d)} · 30 days: ${f(w.last_30d)}`}
              definition={w.definition}
            />
          ))}
          <Tile
            label="Cache hit rate (30d)"
            value={fmtPercent(ai.cache_hit_rate.value)}
            definition={ai.cache_hit_rate.definition}
          />
          <Tile
            label="Tokens (30d)"
            value={fmtInt(ai.tokens_30d.input + ai.tokens_30d.output)}
            sub={`in ${fmtInt(ai.tokens_30d.input)} · out ${fmtInt(ai.tokens_30d.output)}`}
            definition={ai.tokens_30d.definition}
          />
          <Spark
            title="AI cost per day"
            points={series.ai.points}
            field="cost_usd"
            format={fmtUsd}
            definition={series.ai.definition}
          />
        </div>
        <div className="staff-tables">
          <div>
            <h3 className="staff-h3">By feature (30d)</h3>
            <table className="staff-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>Requests</th>
                  <th>Calls</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {ai.by_prompt_mode_30d.rows.map((r) => (
                  <tr key={r.prompt_mode}>
                    <td>{r.prompt_mode || '—'}</td>
                    <td>{fmtInt(r.requests)}</td>
                    <td>{fmtInt(r.llm_calls)}</td>
                    <td>{fmtUsd(r.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="staff-tile__def">{ai.by_prompt_mode_30d.definition}</p>
          </div>
          <div>
            <h3 className="staff-h3">By model (30d)</h3>
            <table className="staff-table">
              <thead>
                <tr>
                  <th>Provider / model</th>
                  <th>Calls</th>
                  <th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {ai.by_model_30d.rows.map((r) => (
                  <tr key={`${r.provider}/${r.model}`}>
                    <td>
                      {r.provider || '—'} / {r.model || '—'}
                    </td>
                    <td>{fmtInt(r.llm_calls)}</td>
                    <td>{fmtUsd(r.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="staff-tile__def">{ai.by_model_30d.definition}</p>
          </div>
        </div>
      </Section>
    </>
  );
}
